import type { DsdEditorMappingReviewJudgement } from "@/apis/dsd-editor/route";
import { cn } from "@/lib/utils";
import { TextChip } from "@/toggle-ui-kit/chip";
import { Check, CheckCheck, Minus } from "lucide-react";
import { DSD_REVIEW_COPY } from "./copy";

// 후보의 주석 관계 칩·일치 표시·저장된 판단 표시 — 패널 카드·검토 모달·근거 창 머리글이 같이 쓴다 (Figma 7730-589281, 7975-523197).
// 색·크기를 바꾸려면 여기만

/** 불일치 아이콘 (Figma, 18px, 채움) — lucide CircleSlash 와 모양이 달라 그대로 옮긴다 */
const MismatchIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    aria-hidden
    className="size-[18px] shrink-0"
  >
    <path
      d="M15.8242 8.99951C15.8242 5.23017 12.7689 2.1748 8.99951 2.1748C7.35786 2.1748 5.85126 2.75374 4.67383 3.71948L14.2788 13.3245C15.2445 12.147 15.8242 10.6411 15.8242 8.99951ZM2.1748 8.99951C2.1748 12.7689 5.23017 15.8242 8.99951 15.8242C10.6411 15.8242 12.147 15.2445 13.3245 14.2788L3.71948 4.67383C2.75374 5.85126 2.1748 7.35786 2.1748 8.99951ZM17.1748 8.99951C17.1748 11.1796 16.3187 13.1581 14.9277 14.6238C14.896 14.6877 14.855 14.7485 14.8018 14.8018C14.7485 14.855 14.6877 14.896 14.6238 14.9277C13.1581 16.3187 11.1796 17.1748 8.99951 17.1748C4.48458 17.1748 0.824219 13.5144 0.824219 8.99951C0.824219 6.82013 1.67897 4.84148 3.06909 3.37598C3.10091 3.31145 3.14357 3.25097 3.19727 3.19727C3.25097 3.14357 3.31145 3.10091 3.37598 3.06909C4.84148 1.67897 6.82013 0.824219 8.99951 0.824219C13.5144 0.824219 17.1748 4.48458 17.1748 8.99951Z"
      fill="currentColor"
    />
  </svg>
);

const TAG_CLASS = {
  positive: "bg-background-neutral-alternative text-label-alternative",
  caution: "bg-amber-100 text-amber-600",
  negative: "bg-rose-100 text-rose-500",
} as const;

/** 주석 관계 칩 (참조 주석·다른 주석·본문). positive 는 초록이 아니라 회색(중립) 칩 */
export const DsdReviewTagChip = ({
  label,
  tone,
  className,
}: {
  label: string;
  tone: keyof typeof TAG_CLASS;
  className?: string;
}) => (
  <TextChip
    size="medium"
    className={cn("shrink-0", TAG_CLASS[tone], className)}
  >
    {label}
  </TextChip>
);

/** 일치(✓✓ 초록) / 불일치(⊘ 주황) — 12px */
export const DsdReviewMatchIndicator = ({
  label,
  positive,
}: {
  label: string;
  positive: boolean;
}) => (
  <span
    className={cn(
      "flex shrink-0 items-center gap-1 caption-1-medium",
      positive ? "text-green-500" : "text-amber-500",
    )}
  >
    {positive ? (
      <CheckCheck aria-hidden className="size-[18px] shrink-0" />
    ) : (
      <MismatchIcon />
    )}
    {label}
  </span>
);

/** 저장된 판단 — 검토 완료(초록 ✓) / 추가 확인 필요(주황 −). 표시만, 클릭 없음 */
export const DsdReviewJudgementStatus = ({
  judgement,
}: {
  judgement: DsdEditorMappingReviewJudgement;
}) => {
  const followUp = judgement === "needs_follow_up";
  const Icon = followUp ? Minus : Check;
  return (
    <span
      className={cn(
        "flex shrink-0 items-center gap-1 caption-1-medium",
        followUp ? "text-amber-500" : "text-green-500",
      )}
    >
      {DSD_REVIEW_COPY.panel.reviewedBadge[judgement]}
      <span className="flex size-6 items-center justify-center">
        <span
          className={cn(
            "flex size-5 items-center justify-center rounded-full text-static-white",
            followUp ? "bg-amber-500" : "bg-green-500",
          )}
        >
          <Icon aria-hidden className="size-4" />
        </span>
      </span>
    </span>
  );
};
